import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Details, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { AssessedArea, DataSource } from '../../../api/workEnvironmentTypes';
import { dataLevelLabel } from '../dataSourcesView';
import { agreement, areaName, assessedPlacement, LEVEL_COLORS, oneDecimal } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

export const RiskAreaItem = ({ area, sources }: { area: AssessedArea; sources: DataSource[] }): ReactElement => {
  const { t, i18n } = useTranslation();
  const placement = assessedPlacement(area);
  return (
    <Details>
      <Details.Summary>
        <span className={classes.areaSummary}>
          <span className={classes.areaTitle}>
            <span className="ds-label">{areaName(area, t)}</span>
            <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
              {t('workEnvironmentRisk.area.meta', placement)}
            </span>
          </span>
          <Tag data-size="sm" data-color={LEVEL_COLORS[placement.level]}>
            {t(`workEnvironmentRisk.matrix.levels.${placement.level}`)}
          </Tag>
        </span>
      </Details.Summary>
      <Details.Content>
        <div className={classes.areaBody}>
          {area.description && <Paragraph data-size="sm">{area.description}</Paragraph>}
          {area.oneReport && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t('workEnvironmentRisk.area.oneReport')}</Paragraph>
            </Alert>
          )}
          {area.adjustment && (
            <Paragraph data-size="sm">
              {t('workEnvironmentRisk.area.adjustmentReason', { ...area, reason: area.adjustment.reason })}
            </Paragraph>
          )}
          {area.skipped !== null && (
            <Paragraph data-size="sm">{t('workEnvironmentRisk.area.skipped', { count: area.skipped })}</Paragraph>
          )}
          <ul className={classes.questionList}>
            {area.questions.map((question) => (
              <li key={question.ref}>
                <span className="ds-paragraph" data-size="sm">
                  {question.text}
                </span>
                <span className={classes.questionFigures}>
                  <span className="ds-paragraph" data-size="xs">
                    {t('workEnvironmentRisk.area.mean')}{' '}
                    <span className="ds-label" data-size="xs">
                      {oneDecimal(question.mean, i18n.language)}
                    </span>
                  </span>
                  <span className="ds-paragraph" data-size="xs">
                    {t('workEnvironmentRisk.area.standardDeviation')}{' '}
                    <span className="ds-label" data-size="xs">
                      {oneDecimal(question.standardDeviation, i18n.language)}
                    </span>
                  </span>
                  <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                    {t(`workEnvironmentRisk.area.agreement.${agreement(question.standardDeviation)}`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {sources.length > 0 && (
            <ul className={classes.followUpList}>
              {sources.map((source) => (
                <li key={source.id} className="ds-paragraph" data-size="sm">
                  {t('workEnvironmentRisk.data.inArea', {
                    title: source.title,
                    type: t(`workEnvironmentRisk.data.types.${source.type}`).toLowerCase(),
                    level: dataLevelLabel(source, t).toLowerCase(),
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Details.Content>
    </Details>
  );
};
